import { useSet } from "@uidotdev/usehooks";
import cn from "classnames";
import { type FC, useCallback, useEffect, useMemo, useRef } from "react";
import type { ContentFileDto } from "shared";
import { useDialog } from "../common/dialog/Dialog";
import { Modal, useModal } from "../common/modal/Modal";
import { Spinner } from "../common/Spinner";
import { Button } from "../components/Button";
import { useMediaActions } from "../content/useMediaActions";
import { useMediaPage } from "../content/useMediaPage";
import {
	MediaUploadDialog,
	type MediaUploadMode,
} from "../media/MediaUploadDialog";
import { useUploadComplete } from "../media/upload";

interface ModalSelectMediaProps {
	onChange: (values: Array<number>) => unknown;
	multiple?: boolean;
	values: Array<number>;
	/** Restrict library picker / upload to YouTube Shorts rules. */
	uploadMode?: MediaUploadMode;
	/** When set, only show matching content types in the library grid. */
	acceptType?: ContentFileDto["type"];
	/** Shown above the grid when media type is restricted (e.g. carousel = images only). */
	mediaHint?: string;
}

function emptyLibraryMessage(
	acceptType: ContentFileDto["type"] | undefined,
): string {
	if (acceptType === "video") {
		return "No videos in the library yet. Upload an MP4, MOV, or WEBM.";
	}
	if (acceptType === "image") {
		return "No images in the library yet.";
	}
	return "No media in the library yet.";
}

function MediaThumb({ file }: { file: ContentFileDto }) {
	const thumbnail = file.thumbnails["256"] ?? file.thumbnails["512"];

	if (file.type === "video") {
		return (
			<video tabIndex={-1} muted preload="metadata" poster={thumbnail}>
				<source src={file.url} type={`video/${file.extension}`} />
			</video>
		);
	}

	return <img src={thumbnail ?? file.url} alt={file.filename} />;
}

export const ModalSelectMedia: FC<ModalSelectMediaProps> = (props) => {
	const {
		values,
		multiple = true,
		uploadMode = "default",
		acceptType,
		mediaHint,
	} = props;
	const { data, fetchNextPage, hasNextPage, isFetchingNextPage } = useMediaPage(
		{ folderId: undefined },
	);
	const selected = useSet<number>(values);
	const { close } = useModal();
	const dialog = useDialog();
	const { invalidateMedia } = useMediaActions();
	const sentinelRef = useRef<HTMLDivElement>(null);
	const isShorts = uploadMode === "youtubeShorts";

	useUploadComplete(async (mediaID) => {
		await invalidateMedia();
		if (!multiple) {
			for (const id of Array.from(selected)) {
				selected.delete(id);
			}
		}
		selected.add(mediaID);
	});

	useEffect(() => {
		const el = sentinelRef.current;
		if (!el) return;

		const observer = new IntersectionObserver(
			(entries) => {
				if (entries[0].isIntersecting && hasNextPage && !isFetchingNextPage) {
					fetchNextPage();
				}
			},
			{ threshold: 0.1 },
		);

		observer.observe(el);
		return () => observer.disconnect();
	}, [hasNextPage, isFetchingNextPage, fetchNextPage]);

	const allFiles = useMemo(() => {
		const files = data?.pages.flatMap((p) => p.files) ?? [];
		if (!acceptType) {
			return files;
		}
		return files.filter((f) => f.type === acceptType);
	}, [data, acceptType]);

	const handleUpload = () => {
		dialog.open(MediaUploadDialog, { mode: uploadMode });
	};

	const handleClick = useCallback(
		(file: ContentFileDto) => {
			if (selected.has(file.id)) {
				selected.delete(file.id);
			} else {
				if (!multiple && selected.size >= 1) {
					for (const id of Array.from(selected)) {
						selected.delete(id);
					}
				}
				selected.add(file.id);
			}
		},
		[selected, multiple],
	);

	const handleSelect = useCallback(() => {
		const allowedIds = new Set(allFiles.map((f) => f.id));
		const ids = Array.from(selected).filter((id) => allowedIds.has(id));
		if (ids.length === 0) {
			return;
		}
		props.onChange(ids);
		close();
	}, [props, selected, close, allFiles]);

	return (
		<Modal size="lg">
			{isShorts && (
				<p className="text-muted mb-2">
					Select or upload one vertical Short (MP4/MOV, 9:16, ≤60s, max 256MB).
				</p>
			)}
			{mediaHint && (
				// biome-ignore lint/a11y/useSemanticElements: Bootstrap alert is not form output
				<div className="alert alert-info py-2 mb-2" role="status">
					{mediaHint}
				</div>
			)}
			<div className="modal-select-media-container mb-3">
				<div className="modal-select-media-scroll">
					{allFiles.length === 0 && !isFetchingNextPage && (
						<p className="text-muted p-3 mb-0">
							{emptyLibraryMessage(acceptType)}
						</p>
					)}
					{allFiles.map((file) => {
						const name = file.filename.trim() || "Untitled";
						return (
							<button
								key={`media-selector-item-${file.id}`}
								className={cn({ selected: selected.has(file.id) })}
								onClick={() => handleClick(file)}
								type="button"
								title={name}
								aria-label={name}
							>
								<span className="modal-select-media-thumb">
									<MediaThumb file={file} />
								</span>
								<span className="modal-select-media-name">{name}</span>
							</button>
						);
					})}
					<div ref={sentinelRef} style={{ height: 1, width: "100%" }} />
					{isFetchingNextPage && (
						<div className="d-flex justify-content-center p-2 w-100">
							<Spinner />
						</div>
					)}
				</div>
			</div>
			<div className="d-flex gap-2">
				<Button full icon="add_circle" onClick={handleUpload}>
					Upload new
				</Button>
				<Button
					full
					onClick={handleSelect}
					icon="cloud_upload"
					disabled={selected.size === 0}
				>
					Select
				</Button>
			</div>
		</Modal>
	);
};
